/**
 * The client's live view of a session. Binds the Socket.IO connection, keeps a
 * local mirror of the authoritative session state, auto-rejoins on reconnect, and
 * exposes typed action emitters. Both the presenter and producer routes use it.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import type {
  HistoryEntry,
  InfoMessage,
  JoinResult,
  LayoutSet,
  Presence,
  PresenceUpdate,
  Project,
  Reanchor,
  Role,
  ScrollControl,
  SessionState,
  SettingsUpdate,
  TimerControl,
} from '@goanna-im/shared';
import { getSocket } from './socket.js';

export interface SessionClientState {
  connected: boolean;
  joined: boolean;
  role: Role | null;
  session: SessionState | null;
  error: string | null;
}

type Action =
  | { t: 'connected'; v: boolean }
  | { t: 'joined'; session: SessionState; role: Role }
  | { t: 'error'; message: string }
  | { t: 'denied'; message: string }
  | { t: 'session'; session: SessionState }
  | { t: 'script'; project: Project }
  | { t: 'scroll'; scroll: SessionState['scroll'] }
  | { t: 'layout'; layout: SessionState['layout'] }
  | { t: 'cue'; message: InfoMessage | null }
  | { t: 'timers'; timers: SessionState['timers'] }
  | { t: 'settings'; settings: SessionState['settings'] }
  | { t: 'record'; on: boolean }
  | { t: 'presence'; presence: Presence[] };

const initial: SessionClientState = {
  connected: false,
  joined: false,
  role: null,
  session: null,
  error: null,
};

function reducer(state: SessionClientState, action: Action): SessionClientState {
  switch (action.t) {
    case 'connected':
      return { ...state, connected: action.v };
    case 'joined':
      return { ...state, joined: true, role: action.role, session: action.session, error: null };
    case 'error':
      return { ...state, error: action.message };
    case 'denied':
      // Access refused (e.g. the 12-hour window closed) — drop back to the join screen.
      return { ...initial, connected: state.connected, error: action.message };
    case 'session':
      return { ...state, session: action.session };
    default:
      break;
  }
  if (!state.session) return state;
  const s = state.session;
  switch (action.t) {
    case 'script':
      return { ...state, session: { ...s, project: action.project } };
    case 'scroll':
      return { ...state, session: { ...s, scroll: action.scroll } };
    case 'layout':
      return { ...state, session: { ...s, layout: action.layout } };
    case 'cue':
      return { ...state, session: { ...s, message: action.message } };
    case 'timers':
      return { ...state, session: { ...s, timers: action.timers } };
    case 'settings':
      return { ...state, session: { ...s, settings: action.settings } };
    case 'record':
      return { ...state, session: { ...s, recording: action.on } };
    case 'presence':
      return { ...state, session: { ...s, presence: action.presence } };
    default:
      return state;
  }
}

export interface SessionActions {
  createSession(role: Role, name: string): Promise<JoinResult>;
  joinSession(roomCode: string, role: Role, name: string): Promise<JoinResult>;
  updateScript(project: Project): void;
  scroll(control: ScrollControl): void;
  reanchor(r: Reanchor): void;
  setLayout(payload: LayoutSet): void;
  showCue(message: InfoMessage): void;
  clearCue(): void;
  timer(control: TimerControl): void;
  updateSettings(patch: SettingsUpdate): void;
  updatePresence(patch: PresenceUpdate): void;
  setRecording(on: boolean): void;
  sendTranscript(text: string): void;
  signoff(): void;
  listHistory(): Promise<HistoryEntry[]>;
  restoreHistory(at: number): Promise<boolean>;
}

export function useSession(): { state: SessionClientState; actions: SessionActions } {
  const [state, dispatch] = useReducer(reducer, initial);
  const socket = useMemo(() => getSocket(), []);
  const rejoinRef = useRef<{ roomCode: string; role: Role; name: string } | null>(null);

  useEffect(() => {
    const onConnect = () => {
      dispatch({ t: 'connected', v: true });
      // Auto-rejoin after a reconnect so we get a fresh snapshot.
      const rj = rejoinRef.current;
      if (rj) {
        socket.emit('session:join', rj, (result) => {
          if (result.ok) dispatch({ t: 'session', session: result.session });
          // A rejoin can be refused if the access window closed since first join —
          // drop the presenter back to the join screen with the reason.
          else {
            rejoinRef.current = null;
            dispatch({ t: 'denied', message: result.error });
          }
        });
      }
    };
    const onDisconnect = () => dispatch({ t: 'connected', v: false });

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('state:snapshot', (session) => dispatch({ t: 'session', session }));
    socket.on('script:updated', (project) => dispatch({ t: 'script', project }));
    socket.on('scroll:state', (scroll) => dispatch({ t: 'scroll', scroll }));
    socket.on('layout:updated', (layout) => dispatch({ t: 'layout', layout }));
    socket.on('cue:shown', (message) => dispatch({ t: 'cue', message }));
    socket.on('cue:cleared', () => dispatch({ t: 'cue', message: null }));
    socket.on('timers:updated', (timers) => dispatch({ t: 'timers', timers }));
    socket.on('settings:updated', (settings) => dispatch({ t: 'settings', settings }));
    socket.on('record:state', ({ on }) => dispatch({ t: 'record', on }));
    socket.on('presence:updated', (presence) => dispatch({ t: 'presence', presence }));
    socket.on('session:error', (payload) => dispatch({ t: 'error', message: payload.message }));

    if (socket.connected) onConnect();

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('state:snapshot');
      socket.off('script:updated');
      socket.off('scroll:state');
      socket.off('layout:updated');
      socket.off('cue:shown');
      socket.off('cue:cleared');
      socket.off('timers:updated');
      socket.off('settings:updated');
      socket.off('record:state');
      socket.off('presence:updated');
      socket.off('session:error');
    };
  }, [socket]);

  const createSession = useCallback(
    (role: Role, name: string) =>
      new Promise<JoinResult>((resolve) => {
        socket.emit('session:create', { role, name }, (result) => {
          if (result.ok) {
            rejoinRef.current = { roomCode: result.session.roomCode, role, name };
            dispatch({ t: 'joined', session: result.session, role });
          } else {
            dispatch({ t: 'error', message: result.error });
          }
          resolve(result);
        });
      }),
    [socket],
  );

  const joinSession = useCallback(
    (roomCode: string, role: Role, name: string) =>
      new Promise<JoinResult>((resolve) => {
        socket.emit('session:join', { roomCode, role, name }, (result) => {
          if (result.ok) {
            rejoinRef.current = { roomCode: result.session.roomCode, role, name };
            dispatch({ t: 'joined', session: result.session, role });
          } else {
            dispatch({ t: 'error', message: result.error });
          }
          resolve(result);
        });
      }),
    [socket],
  );

  const actions = useMemo<SessionActions>(
    () => ({
      createSession,
      joinSession,
      updateScript: (project) => socket.emit('script:update', project),
      scroll: (control) => socket.emit('scroll:control', control),
      reanchor: (r) => socket.emit('scroll:reanchor', r),
      setLayout: (payload) => socket.emit('layout:set', payload),
      showCue: (message) => socket.emit('cue:show', message),
      clearCue: () => socket.emit('cue:clear'),
      timer: (control) => socket.emit('timer:control', control),
      updateSettings: (patch) => socket.emit('settings:update', patch),
      updatePresence: (patch) => socket.emit('presence:update', patch),
      setRecording: (on) => socket.emit('record:set', { on }),
      sendTranscript: (text) => socket.emit('transcript:line', { text }),
      signoff: () => socket.emit('session:signoff'),
      listHistory: () =>
        new Promise<HistoryEntry[]>((resolve) => socket.emit('history:list', (entries) => resolve(entries))),
      restoreHistory: (at) =>
        new Promise<boolean>((resolve) => socket.emit('history:restore', { at }, (ok) => resolve(ok))),
    }),
    [socket, createSession, joinSession],
  );

  return { state, actions };
}
